import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { Search, UserPlus, Trash2, Upload, CheckCircle, AlertCircle, X, Pencil } from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';

type SocietyUnit = {
    id: string;
    unit_number: string;
    full_unit_id: string;
    tower_name?: string;
};

type UserRow = {
    id: string;
    name: string;
    email?: string;
    phone_number?: string;
    role: string;
    society_id?: string;
    units?: { id: string; unit_number: string }[];
};

const unitsRequiredForRole = (role: string) => role === 'resident';

const UnitPicker = ({
    role,
    selectedUnitIds,
    onToggle,
    unitSearch,
    onSearchChange,
    availableUnits,
    unitsLoading,
    activeSocietyId,
}: {
    role: string;
    selectedUnitIds: string[];
    onToggle: (id: string) => void;
    unitSearch: string;
    onSearchChange: (v: string) => void;
    availableUnits: SocietyUnit[];
    unitsLoading: boolean;
    activeSocietyId?: string;
}) => {
    const filteredUnits = useMemo(() => {
        const q = unitSearch.trim().toLowerCase();
        if (!q) return availableUnits;
        return availableUnits.filter(u =>
            u.full_unit_id.toLowerCase().includes(q) ||
            u.unit_number.toLowerCase().includes(q) ||
            (u.tower_name || '').toLowerCase().includes(q)
        );
    }, [availableUnits, unitSearch]);

    return (
        <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">
                Units {unitsRequiredForRole(role) ? '(required for residents)' : '(optional)'}
            </label>
            {selectedUnitIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                    {selectedUnitIds.map(id => {
                        const unit = availableUnits.find(u => u.id === id);
                        const label = unit?.full_unit_id || unit?.unit_number || id;
                        return (
                            <span
                                key={id}
                                className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 px-2 py-0.5 rounded text-[10px] font-medium border border-violet-100"
                            >
                                {label}
                                <button type="button" onClick={() => onToggle(id)} className="hover:text-violet-900">
                                    <X size={12} />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}
            <input
                type="text"
                value={unitSearch}
                onChange={e => onSearchChange(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500 mb-2"
                placeholder="Search units..."
            />
            <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-lg bg-slate-50">
                {unitsLoading ? (
                    <p className="p-3 text-xs text-slate-400">Loading units...</p>
                ) : filteredUnits.length === 0 ? (
                    <p className="p-3 text-xs text-slate-400">
                        {activeSocietyId ? 'No units found for this society.' : 'Select a society to load units.'}
                    </p>
                ) : (
                    filteredUnits.map(unit => (
                        <label
                            key={unit.id}
                            className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-white border-b border-slate-100 last:border-b-0"
                        >
                            <input
                                type="checkbox"
                                checked={selectedUnitIds.includes(unit.id)}
                                onChange={() => onToggle(unit.id)}
                                className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                            />
                            <span className="font-medium text-slate-700">{unit.full_unit_id}</span>
                            {unit.tower_name && (
                                <span className="text-[10px] text-slate-400">{unit.tower_name}</span>
                            )}
                        </label>
                    ))
                )}
            </div>
        </div>
    );
};

const UserManagement = () => {
    const [users, setUsers] = useState<UserRow[]>([]);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [role, setRole] = useState('resident');
    const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
    const [unitSearch, setUnitSearch] = useState('');
    const [availableUnits, setAvailableUnits] = useState<SocietyUnit[]>([]);
    const [editAvailableUnits, setEditAvailableUnits] = useState<SocietyUnit[]>([]);
    const [unitsLoading, setUnitsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [addSocietyId, setAddSocietyId] = useState('');
    const [formError, setFormError] = useState('');
    const [editError, setEditError] = useState('');
    const [editingUser, setEditingUser] = useState<UserRow | null>(null);
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPassword, setEditPassword] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editRole, setEditRole] = useState('resident');
    const [editUnitIds, setEditUnitIds] = useState<string[]>([]);
    const [editUnitSearch, setEditUnitSearch] = useState('');
    const [editSaving, setEditSaving] = useState(false);

    const [profile, setProfile] = useState<any>(null);
    const [societies, setSocieties] = useState<any[]>([]);
    const [selectedSocietyId, setSelectedSocietyId] = useState('');
    const [bulkFile, setBulkFile] = useState<File | null>(null);
    const [bulkUploading, setBulkUploading] = useState(false);
    const [bulkProgress, setBulkProgress] = useState('');
    const [bulkErrors, setBulkErrors] = useState<string[]>([]);
    const [bulkSuccessCount, setBulkSuccessCount] = useState(0);

    const activeSocietyId = profile?.role === 'super_admin'
        ? addSocietyId || selectedSocietyId
        : profile?.society_id;

    useEffect(() => {
        fetchUsers();
        fetchProfile();
    }, []);

    useEffect(() => {
        if (activeSocietyId) {
            fetchUnits(activeSocietyId, setAvailableUnits);
        } else {
            setAvailableUnits([]);
        }
    }, [activeSocietyId]);

    useEffect(() => {
        if (editingUser?.society_id) {
            fetchUnits(editingUser.society_id, setEditAvailableUnits);
        }
    }, [editingUser?.society_id]);

    const fetchUnits = async (societyId: string, setter: (u: SocietyUnit[]) => void) => {
        setUnitsLoading(true);
        try {
            const res = await api.get('/admin/units', {
                params: profile?.role === 'super_admin' ? { society_id: societyId } : {},
            });
            setter(res.data);
        } catch (err) {
            console.error('Failed to fetch units', err);
            setter([]);
        } finally {
            setUnitsLoading(false);
        }
    };

    const fetchProfile = async () => {
        try {
            const res = await api.get('/profiles/me');
            setProfile(res.data);
            if (res.data.role === 'super_admin') {
                const socRes = await api.get('/admin/societies');
                setSocieties(socRes.data);
                if (socRes.data.length > 0) {
                    const firstId = socRes.data[0].id;
                    setSelectedSocietyId(firstId);
                    setAddSocietyId(firstId);
                }
            }
        } catch (err) {
            console.error('Failed to fetch profile', err);
        }
    };

    const fetchUsers = async () => {
        const res = await api.get('/admin/users');
        setUsers(res.data);
    };

    const toggleUnit = (unitId: string, ids: string[], setter: (v: string[]) => void) => {
        setter(ids.includes(unitId) ? ids.filter(id => id !== unitId) : [...ids, unitId]);
    };

    const validateUnits = (r: string, unitIds: string[]) => {
        if (unitsRequiredForRole(r) && unitIds.length === 0) {
            return 'At least one unit is required for residents.';
        }
        return '';
    };

    const resetAddForm = () => {
        setName('');
        setEmail('');
        setPassword('');
        setPhone('');
        setRole('resident');
        setSelectedUnitIds([]);
        setUnitSearch('');
        setFormError('');
    };

    const openEdit = async (user: UserRow) => {
        setEditError('');
        setEditPassword('');
        try {
            const res = await api.get(`/admin/users/${user.id}`);
            const u = res.data;
            setEditingUser(u);
            setEditName(u.name);
            setEditEmail(u.email || '');
            setEditPhone(u.phone_number || '');
            setEditRole(u.role);
            setEditUnitIds((u.units || []).map((un: { id: string }) => un.id));
            setEditUnitSearch('');
        } catch (err: any) {
            alert(err.response?.data?.detail || 'Failed to load user');
        }
    };

    const closeEdit = () => {
        setEditingUser(null);
        setEditError('');
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');
        if (profile?.role === 'super_admin' && !addSocietyId) {
            setFormError('Please select a target society first.');
            return;
        }
        const unitErr = validateUnits(role, selectedUnitIds);
        if (unitErr) {
            setFormError(unitErr);
            return;
        }
        try {
            await api.post('/admin/users', {
                name,
                email,
                role,
                password: password || undefined,
                phone_number: phone || undefined,
                unit_ids: selectedUnitIds,
                ...(profile?.role === 'super_admin' && addSocietyId && { society_id: addSocietyId }),
            });
            resetAddForm();
            fetchUsers();
        } catch (err: any) {
            setFormError(err.response?.data?.detail || err.message || 'Failed to create user');
        }
    };

    const handleEditSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;
        setEditError('');
        const unitErr = validateUnits(editRole, editUnitIds);
        if (unitErr) {
            setEditError(unitErr);
            return;
        }
        setEditSaving(true);
        try {
            await api.patch(`/admin/users/${editingUser.id}`, {
                name: editName,
                email: editEmail,
                role: editRole,
                phone_number: editPhone,
                ...(editPassword ? { password: editPassword } : {}),
                unit_ids: editUnitIds,
            });
            closeEdit();
            fetchUsers();
        } catch (err: any) {
            setEditError(err.response?.data?.detail || err.message || 'Failed to update user');
        } finally {
            setEditSaving(false);
        }
    };

    const handleRemove = async (id: string) => {
        if (!window.confirm('Remove this user?')) return;
        await api.delete(`/admin/users/${id}`);
        fetchUsers();
    };

    const handleBulkUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!bulkFile) return;
        if (profile?.role === 'super_admin' && !selectedSocietyId) {
            alert('Please select a target society first.');
            return;
        }

        setBulkUploading(true);
        setBulkProgress('Reading file...');
        setBulkErrors([]);
        setBulkSuccessCount(0);

        try {
            const text = await bulkFile.text();
            const lines = text.split('\n');
            if (lines.length < 2) {
                throw new Error('CSV file is empty or missing headers');
            }

            const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
            const nameIdx = headers.indexOf('name');
            const emailIdx = headers.indexOf('email');
            const passwordIdx = headers.indexOf('password');
            const roleIdx = headers.indexOf('role');
            const unitsIdx = headers.indexOf('units');

            if (nameIdx === -1 || emailIdx === -1) {
                throw new Error('CSV must contain "name" and "email" columns.');
            }

            const parsedUsers = [];
            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;

                const cols = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(c => c.trim().replace(/^["']|["']$/g, ''));
                if (cols.length < Math.max(nameIdx, emailIdx) + 1) continue;

                const nameVal = cols[nameIdx];
                const emailVal = cols[emailIdx];
                const passwordVal = passwordIdx !== -1 ? cols[passwordIdx] : '';
                const roleVal = roleIdx !== -1 ? cols[roleIdx] || 'resident' : 'resident';
                const unitsStr = unitsIdx !== -1 ? cols[unitsIdx] : '';

                const unit_numbers = unitsStr
                    ? unitsStr.replace(/["']/g, '').split(/[;/]/).map(u => u.trim()).filter(u => u)
                    : [];

                parsedUsers.push({
                    name: nameVal,
                    email: emailVal,
                    password: passwordVal || undefined,
                    role: roleVal,
                    unit_numbers,
                    ...(profile?.role === 'super_admin' && selectedSocietyId && { society_id: selectedSocietyId })
                });
            }

            let success = 0;
            const errors: string[] = [];

            for (let idx = 0; idx < parsedUsers.length; idx++) {
                const user = parsedUsers[idx];
                setBulkProgress(`Importing user ${idx + 1} of ${parsedUsers.length}: ${user.name}`);
                try {
                    await api.post('/admin/users', user);
                    success++;
                    setBulkSuccessCount(success);
                } catch (err: any) {
                    const errMsg = err.response?.data?.detail || err.message || 'Unknown error';
                    errors.push(`Row ${idx + 2} (${user.email}): ${errMsg}`);
                    setBulkErrors([...errors]);
                }
            }

            setBulkProgress(`Import complete! Successfully created ${success} profiles.`);
            setBulkFile(null);
            fetchUsers();
        } catch (err: any) {
            setBulkErrors([err.message]);
            setBulkProgress('Failed to process CSV file.');
        } finally {
            setBulkUploading(false);
        }
    };

    const filteredUsers = users.filter(u =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <PageHeader title="Users & Units" subtitle="Manage society members and staff">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                        type="text"
                        placeholder="Search users..."
                        className="bg-white border border-slate-200 rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-2 focus:ring-violet-500 transition-all outline-none"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
            </PageHeader>

            {editingUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-slate-900">Edit User</h3>
                            <button type="button" onClick={closeEdit} className="text-slate-400 hover:text-slate-600">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleEditSave} className="space-y-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Full Name</label>
                                <input value={editName} onChange={e => setEditName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Email</label>
                                <input value={editEmail} onChange={e => setEditEmail(e.target.value)} type="email" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Phone</label>
                                <input value={editPhone} onChange={e => setEditPhone(e.target.value)} type="tel" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="+91..." />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">New Password</label>
                                <input value={editPassword} onChange={e => setEditPassword(e.target.value)} type="password" autoComplete="new-password" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="Leave blank to keep current" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Role</label>
                                <select value={editRole} onChange={e => setEditRole(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500">
                                    <option value="resident">Resident</option>
                                    <option value="admin">Admin</option>
                                    <option value="support">Support</option>
                                </select>
                            </div>
                            <UnitPicker
                                role={editRole}
                                selectedUnitIds={editUnitIds}
                                onToggle={id => toggleUnit(id, editUnitIds, setEditUnitIds)}
                                unitSearch={editUnitSearch}
                                onSearchChange={setEditUnitSearch}
                                availableUnits={editAvailableUnits}
                                unitsLoading={unitsLoading}
                                activeSocietyId={editingUser.society_id}
                            />
                            {editError && (
                                <p className="text-xs text-red-600 font-medium">{editError}</p>
                            )}
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={closeEdit} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                                    Cancel
                                </button>
                                <button type="submit" disabled={editSaving} className="flex-1 bg-violet-600 text-white py-2.5 rounded-lg font-bold text-sm hover:bg-violet-700 disabled:opacity-50">
                                    {editSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-4 text-violet-600">
                            <UserPlus size={20} />
                            <h3 className="font-bold">Add Member</h3>
                        </div>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Full Name</label>
                                <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="John Doe" required />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Email</label>
                                <input value={email} onChange={e => setEmail(e.target.value)} type="email" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="john@example.com" required />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Password</label>
                                <input value={password} onChange={e => setPassword(e.target.value)} type="password" autoComplete="new-password" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="Leave blank to auto-generate" />
                                <p className="text-[10px] text-slate-400 mt-1">If empty, a random password is set (share it with the user).</p>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Phone</label>
                                <input value={phone} onChange={e => setPhone(e.target.value)} type="tel" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="+91..." />
                            </div>
                            {profile?.role === 'super_admin' && (
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Target Society</label>
                                    <select
                                        value={addSocietyId}
                                        onChange={e => {
                                            setAddSocietyId(e.target.value);
                                            setSelectedUnitIds([]);
                                        }}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500"
                                        required
                                    >
                                        <option value="">Select target society...</option>
                                        {societies.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.short_name || s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Role</label>
                                <select value={role} onChange={e => setRole(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500">
                                    <option value="resident">Resident</option>
                                    <option value="admin">Admin</option>
                                    <option value="support">Support</option>
                                </select>
                            </div>
                            <UnitPicker
                                role={role}
                                selectedUnitIds={selectedUnitIds}
                                onToggle={id => toggleUnit(id, selectedUnitIds, setSelectedUnitIds)}
                                unitSearch={unitSearch}
                                onSearchChange={setUnitSearch}
                                availableUnits={availableUnits}
                                unitsLoading={unitsLoading}
                                activeSocietyId={activeSocietyId}
                            />
                            {formError && (
                                <p className="text-xs text-red-600 font-medium">{formError}</p>
                            )}
                            <button type="submit" className="w-full bg-violet-600 text-white py-2.5 rounded-lg font-bold text-sm hover:bg-violet-700 transition-all shadow-sm">
                                Create Profile
                            </button>
                        </form>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-4 text-violet-600">
                            <Upload size={20} />
                            <h3 className="font-bold">Bulk Upload (CSV)</h3>
                        </div>
                        <form onSubmit={handleBulkUpload} className="space-y-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-500 mb-1 block">Choose CSV File</label>
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={e => setBulkFile(e.target.files?.[0] || null)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs outline-none file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                                    required
                                />
                            </div>
                            {profile?.role === 'super_admin' && (
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Target Society</label>
                                    <select
                                        value={selectedSocietyId}
                                        onChange={e => setSelectedSocietyId(e.target.value)}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500 mb-3"
                                        required
                                    >
                                        <option value="">Select target society...</option>
                                        {societies.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.short_name || s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <p className="text-[10px] text-slate-400 font-medium">
                                Headers: <code className="bg-slate-100 px-1 py-0.5 rounded">name,email,password,role,units</code>
                                <br />
                                Units required for <code className="bg-slate-100 px-1 py-0.5 rounded">resident</code> role. Use <code className="bg-slate-100 px-1 py-0.5 rounded">;</code> to separate units.
                            </p>
                            <button
                                type="submit"
                                disabled={bulkUploading || !bulkFile}
                                className="w-full bg-violet-600 text-white py-2.5 rounded-lg font-bold text-sm hover:bg-violet-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {bulkUploading ? 'Processing...' : 'Upload & Import'}
                            </button>
                        </form>

                        {bulkProgress && (
                            <div className="mt-4 p-3 bg-violet-50 rounded-lg border border-violet-100 text-xs text-violet-700 font-medium">
                                {bulkProgress}
                            </div>
                        )}

                        {bulkSuccessCount > 0 && (
                            <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600 font-semibold">
                                <CheckCircle size={14} /> Created {bulkSuccessCount} profiles.
                            </div>
                        )}

                        {bulkErrors.length > 0 && (
                            <div className="mt-3 space-y-1">
                                <div className="flex items-center gap-1.5 text-xs text-red-600 font-semibold">
                                    <AlertCircle size={14} /> Import Errors:
                                </div>
                                <div className="max-h-32 overflow-y-auto bg-red-50 p-2 rounded-lg border border-red-100 text-[10px] text-red-700 space-y-1 font-mono">
                                    {bulkErrors.map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="lg:col-span-3">
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 text-left font-semibold text-slate-600">Member</th>
                                    <th className="px-6 py-4 text-left font-semibold text-slate-600">Role</th>
                                    <th className="px-6 py-4 text-left font-semibold text-slate-600">Units</th>
                                    <th className="px-6 py-4 text-right font-semibold text-slate-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredUsers.map((u) => (
                                    <tr key={u.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-500 text-xs">
                                                    {u.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-900">{u.name}</p>
                                                    <p className="text-xs text-slate-400 font-medium">{u.email || '—'}</p>
                                                    {u.phone_number && (
                                                        <p className="text-[10px] text-slate-400">{u.phone_number}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={cn(
                                                "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border",
                                                u.role === 'admin' ? "bg-violet-50 text-violet-700 border-violet-100" : u.role === 'support' ? "bg-amber-50 text-amber-700 border-amber-100" : "bg-slate-50 text-slate-600 border-slate-100"
                                            )}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {u.units?.length ? u.units.map((un) => (
                                                    <span key={un.id} className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[10px] font-medium border border-slate-200">
                                                        {un.unit_number}
                                                    </span>
                                                )) : (
                                                    <span className="text-[10px] text-slate-300">—</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => openEdit(u)} className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-all" title="Edit user">
                                                    <Pencil size={16} />
                                                </button>
                                                <button onClick={() => handleRemove(u.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Remove user">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;
